import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

const MOCK_CAMPAIGNS: Campaign[] = [
  {
    id: "1",
    name: "Spring Sale Announcement",
    audience: "all",
    subject: "Our biggest spring sale is live — up to 40% off!",
    status: "sent",
    sent_at: "2026-03-15T10:00:00Z",
    recipients_count: 312,
  },
  {
    id: "2",
    name: "New Product Launch",
    audience: "active",
    subject: "Introducing our brand-new product line",
    status: "scheduled",
    scheduled_at: "2026-04-05T09:00:00Z",
    recipients_count: 198,
  },
  {
    id: "3",
    name: "Re-engagement Campaign",
    audience: "inactive",
    subject: "We miss you — here's 20% off to come back",
    status: "draft",
    recipients_count: 0,
  },
];

type CampaignStatus = "draft" | "scheduled" | "sent";

interface Campaign {
  id: string;
  name: string;
  audience: string;
  subject: string;
  status: CampaignStatus;
  scheduled_at?: string;
  sent_at?: string;
  recipients_count?: number;
}

const STATUS_LABELS: Record<CampaignStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "secondary" },
  scheduled: { label: "Scheduled", cls: "warning" },
  sent: { label: "Sent", cls: "success" },
};

const AUDIENCE_LABELS: Record<string, string> = {
  all: "All affiliates",
  active: "Active affiliates",
  inactive: "Inactive affiliates",
  pending: "Pending approval",
};

export default function EmailCampaignsList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>(MOCK_CAMPAIGNS);
  const [loading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = useCallback((id: string) => {
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    setDeletingId(id);
    setTimeout(() => {
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      setDeletingId(null);
    }, 600);
  }, []);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-0">Email Campaigns</h4>
          <p className="text-muted mb-0 mt-1 small">
            Create and manage bulk email campaigns to your affiliates.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("new")}>
          + New Campaign
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <span className="spinner-border text-primary" role="status" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <p className="text-muted mb-3">No campaigns yet.</p>
            <button className="btn btn-primary" onClick={() => navigate("new")}>
              Create your first campaign
            </button>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Campaign</th>
                  <th>Audience</th>
                  <th>Status</th>
                  <th>Scheduled / Sent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const statusInfo =
                    STATUS_LABELS[c.status] ?? STATUS_LABELS.draft;
                  const ts = c.scheduled_at ?? c.sent_at;
                  return (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.name}</strong>
                        <div className="small text-muted">{c.subject}</div>
                      </td>
                      <td className="small">
                        {AUDIENCE_LABELS[c.audience] ?? c.audience}
                      </td>
                      <td>
                        <span className={`badge bg-${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {ts ? new Date(ts).toLocaleString() : "—"}
                      </td>
                      <td className="text-end">
                        {c.status !== "sent" && (
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => navigate(c.id)}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          disabled={deletingId === c.id}
                          onClick={() => handleDelete(c.id)}
                        >
                          {deletingId === c.id ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
